import React, { useState, useEffect, useCallback, useContext } from 'react';
import { TableProps } from 'components';
import { HistoryController, UserController } from 'api';
import moment from 'moment';
import {
  ParsedUserNoPopulate,
  HistoryCreationMethod,
} from '@bb/domain/dist/types';

import { AuthContext } from 'contexts';
import FormContext from '../../UserViewPage/FormContext';
import { BorderedTable } from './StyledComponents';
import TableEntry from './TableEntry';
import { buildSuggestedLogItems, buildSuggestedUpdateLabel } from './helpers';

interface TableRecordType {
  field: string;
  from: string;
  to: string;
  user: string;
  date: string;
}

const columns: TableProps['columns'] = [
  {
    title: 'FIELD',
    dataIndex: 'field',
    key: 'field',
    width: 130,
    render: value => <TableEntry value={value} />,
  },
  {
    title: 'FROM',
    dataIndex: 'from',
    key: 'from',
    width: 110,
    render: value => <TableEntry value={value} breakAll />,
  },
  {
    title: 'TO',
    dataIndex: 'to',
    key: 'to',
    width: 110,
    render: value => <TableEntry value={value} breakAll />,
  },
  {
    title: 'USER',
    dataIndex: 'user',
    key: 'user',
    width: 150,
    render: value => <TableEntry value={value} />,
  },
  {
    title: 'DATE',
    dataIndex: 'date',
    key: 'date',
    width: 160,
    render: value => <TableEntry value={value} />,
  },
];

interface DetailsTableProps {
  userId: string;
}

const DetailsTable = ({ userId }: DetailsTableProps) => {
  const [userDetails, setUserDetails] = useState<TableProps['dataSource']>([]);
  const { fetchedUserState } = useContext(FormContext);
  const { auth } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);

  const fetchPage = useCallback(async () => {
    const [data, suggestions] = await Promise.all([
      HistoryController.findLog({
        type: 'User',
        id: userId,
      }),
      UserController.getSuggestionsForUser({
        userId,
      }),
    ]);

    const suggestionLogItems = buildSuggestedLogItems(
      suggestions,
      fetchedUserState,
    );

    const actions: (TableRecordType & { key: string })[] = [];
    const isDeletedByBrokerBot = ({
      creationMethod,
      field,
    }: {
      creationMethod?: HistoryCreationMethod;
      field?: string;
    }) => field === 'User Deleted' && creationMethod === 'BrokerBot';

    const parseDiffData = ({
      type,
      entry,
      field,
      creationMethod,
      location,
    }: {
      type: 'from' | 'to';
      entry: any;
      field?: string;
      creationMethod?: HistoryCreationMethod;
      location?: string;
    }) => {
      if (type === 'to' && isDeletedByBrokerBot({ creationMethod, field }))
        return 'Deleted';
      if (field === 'Location')
        return auth?.org?.locations[Number(entry)].name || entry;
      if (field === 'Address') {
        return (
          auth?.org?.addresses[Number(location)][Number(entry)]?.address ||
          entry
        );
      }
      return entry;
    };

    data?.forEach(logEntry => {
      // ENG-5742: possible location/address scenarios:

      // 1 - Log entry only contains Location diff (ie. Address index happens to be the same)
      // Solution: `diff.address` will contain index, add address entry to history

      // 2 - Log entry only contains Address diff (ie. Location is the same)
      // Solution: `diff.location` will contain index, use to grab correct addresses

      // 3 - Log entry contains both Location and Address update
      // Solution: to display proper Address diff, the 'from' fields needs old location, 'to' needs new location

      const locationDiff = logEntry.diffs.find(d => d.field === 'Location');

      logEntry.diffs.forEach(diff => {
        const user = logEntry.user as ParsedUserNoPopulate;
        const { field, from, to, location, address } = diff;
        actions.push({
          key: `${logEntry.updatedAt}-${field}-${to}`,
          field: isDeletedByBrokerBot({
            field,
            creationMethod: logEntry.creationMethod,
          })
            ? 'User Transferred'
            : field,
          from: parseDiffData({
            type: 'from',
            entry: from,
            field,
            location: location || locationDiff?.from,
          }),
          to: parseDiffData({
            type: 'to',
            entry: to,
            field,
            creationMethod: logEntry.creationMethod,
            location: location || locationDiff?.to,
          }),
          user: logEntry.creationMethod
            ? logEntry.creationMethod
            : `${user.firstName ?? ''} ${user.lastName ?? ''}`,
          date: logEntry.updatedAt || diff.updatedAt,
        });

        // From (1) above - add new Address entry to history
        if (
          field === 'Location' &&
          !logEntry.diffs.some(_diff => _diff.field === 'Address')
        ) {
          actions.push({
            key: `${logEntry.updatedAt}-Address-${to}`,
            field: 'Address',
            from: parseDiffData({
              type: 'from',
              field: 'Address',
              entry: address,
              location: from,
            }),
            to: parseDiffData({
              type: 'to',
              field: 'Address',
              entry: address,
              location: to,
            }),
            user: logEntry.creationMethod
              ? logEntry.creationMethod
              : `${user.firstName ?? ''} ${user.lastName ?? ''}`,
            date: logEntry.updatedAt || diff.updatedAt,
          });
        }
      });
    });

    suggestionLogItems.forEach(logEntry => {
      actions.push({
        key: logEntry.key,
        field: buildSuggestedUpdateLabel(logEntry.status, logEntry.type),
        from: logEntry.from,
        to: logEntry.to,
        user: logEntry.user,
        date: logEntry.date?.toString() ?? '',
      });
    });

    const sortedActions = actions
      .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf())
      .map(action => ({
        ...action,
        date: moment(action.date).format('MMM D YYYY, h:mma'),
      }));

    const filteredActions = sortedActions.filter(
      action => !(action.field === 'Address' && action.from === action.to),
    );

    setUserDetails(filteredActions || []);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  return (
    <BorderedTable
      dataSource={userDetails}
      columns={columns}
      pagination={{
        pageSize: 5,
        showSizeChanger: false,
        position: ['bottomLeft'],
      }}
      loading={loading}
      type="primary"
      hideShadow
      scroll={{ x: true }}
    />
  );
};

export default DetailsTable;
